import { LoggerService } from '@app/shared/services/logger.service';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { HandleError, HttpErrorHandler } from '@app/shared/services/http-error-handler.service';
import { Store } from '@ngrx/store';
import { HeadersSetterHelper } from '@app/shared/helpers/http-headers-setter-helper';
import { Observable, Subject } from 'rxjs';
import { ISpringResponseBody, observeTypes } from '@app/shared/interfaces/response-interfaces';
import { AppConfig } from '@app/config/app.config';

import { catchError, map, takeUntil } from 'rxjs/operators';
import { IUserSettings, LoginMethodStatus } from './settings';
import { IConfirmData } from '@app/core/info/confirm/confirm';
import { iconCodes } from '@app/shared/cqes-icon/cqes-icon.component';
import { ConfirmComponent } from '@app/core/info/confirm/confirm.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { InfoService } from '@app/core/info/info.service';
import { ActionAuthAlternativeLoginReminded, ActionAuthAlternativeLoginRemindedSuccess, SessionStorageService } from '@app/core';
import { AuthenticationService } from '@app/core/auth/authentication.service';

@Injectable()
export class SettingsService {
  private handleError: HandleError;
  private headerHelper: any;
  private confirmDialog: MatDialogRef<ConfirmComponent>;

  isEmergencyLogin: boolean;
  isIE11: boolean;
  isCertInvalid: boolean;
  unsubscribe$: Subject<void> = new Subject<void>();
  constructor(
    private http: HttpClient,
    private httpErrorHandler: HttpErrorHandler,
    private info: InfoService,
    private store: Store<any>,
    private router: Router,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    public translateService: TranslateService,
    private logger: LoggerService,
    private sessionStorageService: SessionStorageService,
    public auth: AuthenticationService,
  ) {
    this.handleError = this.httpErrorHandler.createHandleError('SettingsService');
    this.headerHelper = new HeadersSetterHelper(this.store);

    const ua = navigator.userAgent;
    this.isIE11 = ua.indexOf('MSIE ') > -1 || ua.indexOf('Trident/') > -1;
  }

  registerEmail(email: string): Observable<ISpringResponseBody> {
    return this.http.post<ISpringResponseBody>(
      AppConfig.endpoints.profile.emailValidation,
      { email },
      this.headerHelper.options(observeTypes.BODY)
    )
      .pipe(
        catchError(this.handleError('registerEmail')),
        map((res: ISpringResponseBody) => {
          this.logger.log(res);
          return res;
        }));
  }

  // this method triggers the verification process(sending code to phone number)
  registerPhoneNumber(phoneNumber: string): Observable<ISpringResponseBody> {
    return this.http.post<ISpringResponseBody>(
      AppConfig.endpoints.profile.phoneNumber,
      { phoneNumber },
      this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('registerPhoneNumber')),
      map((res: ISpringResponseBody) => {
        this.logger.log(res);
        return res;
      }));
  }

  // this method verifies the entered code and marks the phone number as verified.
  verifyPhoneNumber(code: string): Observable<ISpringResponseBody> {
    return this.http.post<ISpringResponseBody>(
      AppConfig.endpoints.profile.validateOTPCode,
      { code },
      this.headerHelper.options(observeTypes.BODY)
    )
      .pipe(
        catchError(this.handleError('verifyPhoneNumber')),
        map((res: ISpringResponseBody) => {
          this.logger.log(res);
          return res;
        }));
  }

  verifyEmail(code: string): Observable<ISpringResponseBody> {
    return this.http.post<ISpringResponseBody>(
      AppConfig.endpoints.profile.validateEmail,
      { code },
      this.headerHelper.options(observeTypes.BODY)
    )
      .pipe(
        catchError(this.handleError('verifyEmail')),
        map((res: ISpringResponseBody) => {
          this.logger.log(res);
          return res;
        }));
  }

  // if browser is IE11 hide icon in dialog window
  toggleIcon(iconCode: string) {
    if (this.isIE11) {
      return '';
    } else {
      return iconCode;
    }
  }

  public checkEmailAndPhoneStatus(settings: IUserSettings, loading: boolean) {
    console.log('settings', settings)
    this.isCertInvalid = settings.hasCqes
    if (loading === false) {
      if (settings == undefined) {
        settings = this.sessionStorageService.getItem("settings");
      }
      if (this.isCertInvalid == false) {
        return;
      }
      const isEmailActive = settings.loginEmails &&
        settings.loginEmails.findIndex(e => e.status === LoginMethodStatus.active) > -1;

      const isPhoneActive = settings.loginPhoneNumbers &&
        settings.loginPhoneNumbers.findIndex(p => p.status === LoginMethodStatus.active) > -1;
      if (!isEmailActive || !isPhoneActive) {
        this.translateService.get('registerEmailAndPhone').subscribe(translations => {
          this.info.dismissAll();
          this.confirmDialog = this.dialog.open(

            ConfirmComponent,
            {
              data: {
                icon: this.toggleIcon(iconCodes.account_circle),
                title: translations['title'],
                question: translations['description'],
                confirmTxt: translations['yes'],
                cancelTxt: translations['notNow'],
                confirmAction: () => this.operationResult(true)
              } as IConfirmData
            });
          this.confirmDialog.afterClosed().subscribe(result => {
            if (result !== true) {
              this.operationResult(false);
            }
          });
        });
      } else {
        if (!this.sessionStorageService.getItem("accounts")) {
          this.router.navigateByUrl('/manage');
        }
      }
    }
  }

  private operationResult(success: boolean) {
    this.confirmDialog.close(success);
    if (success) {
      this.store.dispatch(new ActionAuthAlternativeLoginRemindedSuccess());
      this.router.navigate(
        ['../' + AppConfig.routes.profile.main + '/' + AppConfig.routes.profile.settings],
        { relativeTo: this.route }
      );
    } else {
      if (!this.sessionStorageService.getItem("accounts")) {
        this.router.navigateByUrl('/manage');
      }
    }
  }

  addNewEmail(email: any): Observable<ISpringResponseBody> {
    return this.http.post<ISpringResponseBody>(
      AppConfig.endpoints.profile.email,
      email,
      this.headerHelper.options(observeTypes.BODY)
    )
      .pipe(
        catchError(this.handleError('addEmail')),
        map((res: ISpringResponseBody) => {
          this.logger.log(res);
          return res;
        }));
  }

  deleteEmail(email: string): Observable<ISpringResponseBody> {
    let params = new HttpParams();
    params = params.set('email', email);

    return this.http.delete<ISpringResponseBody>(
      AppConfig.endpoints.profile.email,
      this.headerHelper.paramsOptions(observeTypes.BODY, params)
    )
      .pipe(
        catchError(this.handleError('deleteEmails')),
        map((res: ISpringResponseBody) => {
          this.logger.log(res);
          return res;
        }));
  }

  addNewPhone(phone: any): Observable<ISpringResponseBody> {
    return this.http.post<ISpringResponseBody>(
      AppConfig.endpoints.profile.phone,
      phone,
      this.headerHelper.options(observeTypes.BODY)
    )
      .pipe(
        catchError(this.handleError('addPhone')),
        map((res: ISpringResponseBody) => {
          this.logger.log(res);
          return res;
        }));
  }

  deletePhone(phoneId: number): Observable<ISpringResponseBody> {
    return this.http.delete<ISpringResponseBody>(
      AppConfig.endpoints.profile.phone + '/' + phoneId,
      this.headerHelper.options(observeTypes.BODY)
    )
      .pipe(
        catchError(this.handleError('deletePhone')),
        map((res: ISpringResponseBody) => {
          this.logger.log(res);
          return res;
        }));
  }

  loadSettings(): Observable<ISpringResponseBody> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.profile.settings,
      this.headerHelper.options(observeTypes.BODY)
    )
      .pipe(
        catchError(this.handleError('addEmail')),
        map((res: ISpringResponseBody) => {
          this.logger.log(res);
          return res;
        }));
  }

}
